/**
 * Referer Utilities
 * Funciones para extraer y procesar el referer del navegador
 * Principio: Single Responsibility - Solo manejo de referer
 */

/**
 * Obtiene el referer actual del documento
 * @returns {string|null} URL del referer o null si no existe
 */
export function getDocumentReferer() {
  try {
    return document.referrer || null;
  } catch (error) {
    console.warn('[Referer Utils] Error getting document referer:', error);
    return null;
  }
}

/**
 * Extrae el host/dominio de una URL completa
 * @param {string} url - URL completa (ej: https://chat.owly.aulasneo.com/path)
 * @returns {string|null} Host/dominio (ej: chat.owly.aulasneo.com)
 */
export function extractHostFromUrl(url) {
  if (!url || typeof url !== 'string') return null;

  try {
    const urlObj = new URL(url);
    return urlObj.host; // Incluye hostname + port si existe
  } catch (error) {
    console.warn('[Referer Utils] Invalid URL:', url, error);
    return null;
  }
}

/**
 * Extrae el hostname (sin puerto) de una URL
 * @param {string} url - URL completa
 * @returns {string|null} Hostname sin puerto
 */
export function extractHostnameFromUrl(url) {
  if (!url || typeof url !== 'string') return null;

  try {
    const urlObj = new URL(url);
    return urlObj.hostname;
  } catch (error) {
    console.warn('[Referer Utils] Invalid URL:', url, error);
    return null;
  }
}

/**
 * Extrae el origen (protocolo + host) de una URL
 * @param {string} url - URL completa
 * @returns {string|null} Origen (ej: https://chat.owly.aulasneo.com)
 */
export function extractOriginFromUrl(url) {
  if (!url || typeof url !== 'string') return null;

  try {
    const urlObj = new URL(url);
    return urlObj.origin;
  } catch (error) {
    console.warn('[Referer Utils] Invalid URL:', url, error);
    return null;
  }
}

/**
 * Valida si una URL es válida y tiene un formato correcto
 * @param {string} url - URL a validar
 * @returns {boolean} True si es válida
 */
export function isValidUrl(url) {
  if (!url || typeof url !== 'string') return false;

  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
}

/**
 * Obtiene el host del referer actual del documento
 * @returns {string|null} Host del referer o null
 */
export function getRefererHost() {
  const referer = getDocumentReferer();
  if (!referer) return null;
  return extractHostFromUrl(referer);
}

/**
 * Obtiene el hostname del referer actual del documento
 * @returns {string|null} Hostname del referer o null
 */
export function getRefererHostname() {
  const referer = getDocumentReferer();
  if (!referer) return null;
  return extractHostnameFromUrl(referer);
}

/**
 * Obtiene el origen del referer actual del documento
 * @returns {string|null} Origen del referer o null
 */
export function getRefererOrigin() {
  const referer = getDocumentReferer();
  if (!referer) return null;
  return extractOriginFromUrl(referer);
}

/**
 * Verifica si el referer proviene del mismo dominio
 * @returns {boolean} True si es same-origin
 */
export function isSameOriginReferer() {
  try {
    const referer = getDocumentReferer();
    if (!referer) return false;

    const refererOrigin = extractOriginFromUrl(referer);
    const currentOrigin = window.location.origin;

    return refererOrigin === currentOrigin;
  } catch (error) {
    console.warn('[Referer Utils] Error checking same origin:', error);
    return false;
  }
}

/**
 * Verifica si hay un referer válido disponible
 * @returns {boolean} True si hay referer válido
 */
export function hasValidReferer() {
  const referer = getDocumentReferer();
  return referer !== null && referer !== '' && isValidUrl(referer);
}

/**
 * Extrae el dominio base eliminando el primer subdominio
 * Ejemplos:
 *   - apps.campus.aulasneo.com → campus.aulasneo.com
 *   - chat.owly.aulasneo.com → owly.aulasneo.com
 *   - aulasneo.com → aulasneo.com (sin cambios si no hay subdominio)
 *   - localhost → localhost (sin cambios)
 * 
 * @param {string} hostname - Hostname completo (ej: apps.campus.aulasneo.com)
 * @returns {string|null} Dominio base sin primer subdominio
 */
export function removeFirstSubdomain(hostname) {
  if (!hostname || typeof hostname !== 'string') return null;

  // Si es localhost o IP, retornar sin cambios
  if (hostname === 'localhost' || /^\d+\.\d+\.\d+\.\d+$/.test(hostname)) {
    return hostname;
  }

  // Split por puntos
  const parts = hostname.split('.');

  // Si tiene 2 o menos partes (ej: aulasneo.com), retornar sin cambios
  if (parts.length <= 2) {
    return hostname;
  }

  // Si tiene 3+ partes, remover el primer subdominio
  // Ejemplo: ['apps', 'campus', 'aulasneo', 'com'] → ['campus', 'aulasneo', 'com']
  return parts.slice(1).join('.');
}

/**
 * Obtiene el dominio base del referer (sin el primer subdominio)
 * @returns {string|null} Dominio base o null
 */
export function getRefererBaseDomain() {
  const hostname = getRefererHostname();
  if (!hostname) return null;
  return removeFirstSubdomain(hostname);
}

/**
 * Extrae el dominio base de una URL (sin el primer subdominio)
 * @param {string} url - URL completa
 * @returns {string|null} Dominio base sin primer subdominio
 */
export function extractBaseDomainFromUrl(url) {
  const hostname = extractHostnameFromUrl(url);
  if (!hostname) return null;
  return removeFirstSubdomain(hostname);
}
